import React, { useState, useEffect } from 'react';
import { useAuth } from '../../Common/Configurations/AuthContext';
import apiService from '../../Common/Configurations/APIService';
import config from '../../Common/Configurations/APIConfig';

const initialProfileState = {
    name: '',
    email: '',
    phoneNumber: ''
};

const EditProfileForm = () => {
    const { user } = useAuth();
    const [profileData, setProfileData] = useState(initialProfileState);
    const [loading, setLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    
    // --- 1. State declarations ---
    const [errors, setErrors] = useState({}); // This was missing
    const [apiError, setApiError] = useState(''); // Renamed 'error' to be more specific
    const [success, setSuccess] = useState('');

    useEffect(() => {
        const fetchProfile = async () => {
            if (!user?.id) {
                setLoading(false);
                return;
            };
            
            setLoading(true); 
            try {
                const data = await apiService.getSingle(config.GETUSERPROFILE);
                setProfileData(data);
            } catch (err) {
                setApiError("Failed to fetch profile data.");
            } finally {
                setLoading(false);
            }
        };
        fetchProfile();
    }, [user]);

    useEffect(() => {
        let timer;
        if (success) {
            timer = setTimeout(() => {
                setSuccess(''); 
            }, 5000); 
        }
        return () => clearTimeout(timer);
    }, [success]); 

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setProfileData({ ...profileData, [name]: value });
        
        // Clear validation error for the field being edited
        if (errors[name]) {
            setErrors(prevErrors => ({ ...prevErrors, [name]: undefined }));
        }

        // Clear API-level messages
        if (apiError) setApiError('');
        if (success) setSuccess('');
    };

    // --- 2. Corrected Validation Function ---
    const validate = () => {
        let tempErrors = {};
        // Use profileData, not values
        if (!profileData.name?.trim()) {
            tempErrors.name = "Full name is required.";
        }
        if (!profileData.phoneNumber?.trim()) {
            tempErrors.phoneNumber = "Phone number is required.";
        } else if (!/^\d{10,}$/.test(profileData.phoneNumber)) { // Optional: Regex for at least 10 digits
            tempErrors.phoneNumber = "Please enter a valid phone number.";
        }

        setErrors(tempErrors);
        return Object.keys(tempErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setApiError('');
        setSuccess('');

        // The validate() function is now called correctly
        if (validate()) {
            setIsSaving(true);
            try {
                const payload = {
                    userId: user.id,
                    name: profileData.name,
                    email: profileData.email,
                    phoneNumber: profileData.phoneNumber,
                    userName: profileData.userName || profileData.email,
                    isActive: profileData.isActive,
                    branchId: profileData.branchId,
                };
                await apiService.update(config.UPDATEUSER, payload);
                setSuccess("Profile updated successfully!");
            } catch (err) {
                setApiError("Failed to update profile.");
            } finally {
                setIsSaving(false);
            }
        }
    };

    // Helper to apply Bootstrap's 'is-invalid' class
    const applyErrorClass = (field) => (errors[field] ? 'is-invalid' : '');

    return (
        <div className="profile-card h-100 position-relative">
            {loading && (
                <div className="form-loader-overlay">
                    <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </div>
                </div>
            )}

            <h4 className="card-title mb-4">Edit Profile</h4>
            
            <fieldset disabled={loading}>
                <form onSubmit={handleSubmit} noValidate>
                    {apiError && <div className="alert alert-danger">{apiError}</div>}
                    {success && <div className="alert alert-success">{success}</div>}
                    
                    <div className="mb-3">
                        <label htmlFor="name" className="form-label">Full Name</label>
                        <input 
                            type="text" 
                            className={`form-control ${applyErrorClass('name')}`} 
                            id="name" 
                            name="name" 
                            value={profileData.name} 
                            onChange={handleInputChange} 
                        />
                        {/* 3. Using Bootstrap's standard error display */}
                        {errors.name && <div className="invalid-feedback d-block">{errors.name}</div>}
                    </div>

                    <div className="mb-3">
                        <label htmlFor="email" className="form-label">Email Address</label>
                        <input 
                            type="email" 
                            className="form-control" 
                            id="email" 
                            name="email" 
                            value={profileData.email} 
                            readOnly 
                            title="Email address cannot be changed." 
                        />
                    </div>

                    <div className="mb-3">
                        <label htmlFor="phoneNumber" className="form-label">Phone Number</label>
                        <input 
                            type="tel" 
                            className={`form-control ${applyErrorClass('phoneNumber')}`} 
                            id="phoneNumber" 
                            name="phoneNumber" 
                            value={profileData.phoneNumber} 
                            onChange={handleInputChange} 
                        />
                        {/* 3. Using Bootstrap's standard error display */}
                        {errors.phoneNumber && <div className="invalid-feedback d-block">{errors.phoneNumber}</div>}
                    </div>
                    
                    <div className="text-end mt-4">
                        <button type="submit" className="btn btn-primary" disabled={loading || isSaving}>
                            {isSaving ? 'Saving...' : 'Save Changes'}
                        </button>
                    </div>
                </form>
            </fieldset>
        </div>
    );
};

export default EditProfileForm;

